import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import useAnalyticsService from "../../../services/AnalyticsService";
import { useUrlDateRange } from "../../../hooks/useUrlDateRange";
import DateRangeToolbar from "../../../components/DateRangeToolbar";
import { EnterpriseStreamSummary } from "../../../types/Analytics";
import Ghost from "../../../components/Ghost";

const minutesToHours = (minutes: number) => {
  const hours = Math.floor(minutes / 60);
  const mins = (minutes % 60).toFixed(0);

  return `${hours}h ${mins}m`;
};

export default function AdminAnalyticsEnterpriseStreamsPage() {
  const { getEnterpriseStreamsSummary } = useAnalyticsService();
  const { startDate, endDate, setRange } = useUrlDateRange(7);
  const [isLoading, setIsLoading] = useState(false);
  const [rows, setRows] = useState<EnterpriseStreamSummary[] | null>(null);
  const navigate = useNavigate();

  const getData = async () => {
    if (!startDate || !endDate || isLoading) {
      return;
    }

    setIsLoading(true);

    try {
      const result = await getEnterpriseStreamsSummary(startDate, endDate);
      setRows(result);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!startDate || !endDate) return;

    getData();
  }, [startDate, endDate]);

  const selectExperience = (experienceId: string) => {
    navigate(`/analytics/enterprise-streams/${experienceId}?startDate=${startDate ? startDate.toISOString().slice(0, 10) : ""}&endDate=${endDate ? endDate.toISOString().slice(0, 10) : ""}`);
  };

  return (
    <div className="grid grid-cols gap-4">
      <h2 className="text-2xl">Enterprise Streams</h2>
      <section>
        {startDate && endDate && (
          <DateRangeToolbar
            startDate={startDate}
            endDate={endDate}
            onChange={setRange}
          />
        )}
      </section>

      <section className="grid grid-cols gap-4">
        {isLoading && <Ghost additionalClasses="w-full h-64" />}
        {!isLoading && rows && rows.length === 0 && (
          <p>No enterprise experiences found.</p>
        )}
        {!isLoading && rows && rows.length > 0 && (
          <table className="table-auto w-full">
            <thead>
              <tr>
                <th className="font-normal text-left p-2">Experience</th>
                <th className="font-normal text-left p-2">Data Session Time</th>
                <th className="font-normal text-left p-2">Streaming Time</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr
                  key={row.experienceId}
                  onClick={() => selectExperience(row.experienceId)}
                  className="border-t border-cyan-900 hover:bg-cyan-300/25 cursor-pointer"
                >
                  <td className="p-2">{row.displayName || row.experienceId}</td>
                  <td className="p-2">{minutesToHours(row.dataSessionMinutes)}</td>
                  <td className="p-2">{minutesToHours(row.streamingSessionMinutes)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
}
